import React, { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useGenesisState } from '../hooks/useGenesisState'

export const SovereignPortal: React.FC = () => {
  const isPortalOpen = useGenesisState(s => s.isPortalOpen)
  const setLastObservation = useGenesisState(s => s.setLastObservation)
  const scaleRef = useRef(0)
  const groupRef = useRef<THREE.Group>(null)
  const outerRingRef = useRef<THREE.Mesh>(null)
  const innerRingRef = useRef<THREE.Mesh>(null)
  const timeRef = useRef(0)
  const hasReportedRef = useRef(false)

  useFrame((_, delta) => {
    if (!groupRef.current || !outerRingRef.current || !innerRingRef.current) return
    timeRef.current += delta

    // Animate scale from 0 → 1 over 3 seconds when portal opens
    if (isPortalOpen && scaleRef.current < 1) {
      scaleRef.current = Math.min(1, scaleRef.current + delta / 3)
    } else if (!isPortalOpen && scaleRef.current > 0) {
      scaleRef.current = Math.max(0, scaleRef.current - delta / 2)
    }

    // Single group scale drives all meshes together
    groupRef.current.scale.setScalar(scaleRef.current)

    // Sensor: report when portal reaches full scale
    if (isPortalOpen && scaleRef.current >= 0.99 && !hasReportedRef.current) {
      hasReportedRef.current = true
      setLastObservation('PORTAL: Dimensional rift fully stabilized. Destination locked.')
    }
    if (!isPortalOpen) {
      hasReportedRef.current = false
    }

    // Slow rotation for the rift
    outerRingRef.current.rotation.z += delta * 0.4
    innerRingRef.current.rotation.z -= delta * 0.6

    // Pulsing emissive intensity on outer ring
    const mat = outerRingRef.current.material as THREE.MeshStandardMaterial
    if (mat.emissiveIntensity !== undefined) {
      mat.emissiveIntensity = 1.5 + Math.sin(timeRef.current * 3) * 0.5
    }
  })

  // Position far in the distance, slightly above centre
  return (
    <group ref={groupRef} position={[0, 2, -100]} scale={0}>
      {/* Outer ring */}
      <mesh ref={outerRingRef}>
        <torusGeometry args={[8, 0.6, 32, 128]} />
        <meshStandardMaterial
          color="#001122"
          emissive="#00FFFF"
          emissiveIntensity={1.5}
          metalness={0.9}
          roughness={0.1}
          transparent
          opacity={0.9}
        />
      </mesh>

      {/* Inner ring — counter-rotates for depth */}
      <mesh ref={innerRingRef}>
        <torusGeometry args={[5.5, 0.3, 32, 128]} />
        <meshStandardMaterial
          color="#000811"
          emissive="#00DDFF"
          emissiveIntensity={1.2}
          metalness={0.8}
          roughness={0.15}
          transparent
          opacity={0.75}
        />
      </mesh>

      {/* Portal fill — dark void disc */}
      <mesh position={[0, 0, -0.1]}>
        <circleGeometry args={[7.5, 64]} />
        <meshStandardMaterial
          color="#000508"
          transparent
          opacity={0.85}
          depthWrite={false}
        />
      </mesh>
    </group>
  )
}

export default SovereignPortal
